import {
  atlassianGatewayBaseUrl,
  atlassianGatewayPattern,
  atlassianServiceAccountApiTokenSteps,
  atlassianServiceAccountOAuth,
  atlassianServiceAccountOAuthSteps,
} from "../atlassianServiceAccount";
import { basicAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const keywords = "atlassian issues tickets bugs epics tasks project tracking";

export const jira = defineProduct({
  name: "Jira",
  tagLine: "Issue and project tracking",
  logo,
  tags: ["featured", "it", "productivity"],
  variants: [
    productVariant({
      identifier: "jira",
      authLabel: "API token without scopes",
      keywords,
      protocol: "http",
      defaultUrlPattern: () => "https://[^/]*\\.atlassian\\.net/rest/api/.*",
      auth: (app) =>
        basicAuth({
          username: app.config.email,
          password: app.secret.apiToken,
        }),
      steps: [
        {
          title: "Create an API token",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in as the Atlassian user whose permissions the connector should use, then open [API tokens](https://id.atlassian.com/manage-profile/security/api-tokens). This connection type uses the user’s email address and an API token without scopes. For a service account connection, choose **Service account API token** or **Service account OAuth 2.0**. Give the user only the Jira permissions needed for the connector’s requests.\n\nClick **Create API token**, then name the token and choose an expiration date.",
            },
          ],
        },
        {
          title: "Copy and paste the API token here",
          items: [
            {
              kind: "input",
              label: "API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
            },
          ],
        },
        {
          title: "Enter the account’s email address",
          items: [
            {
              kind: "input",
              label: "API token account email",
              placeholder: "you@example.com",
              required: true,
              target: { kind: "configuration", field: "email" },
            },
          ],
        },
        {
          title: "Enter your Jira account domain",
          items: [
            {
              kind: "hostname",
              label: "Jira account domain",
              placeholder: "example.atlassian.net",
              required: true,
              target: { kind: "configuration", field: "domain" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ domain }) => `https://${domain}/rest/api/3/myself`,
      },
      publicEnvVars: ({ domain, email }) => ({
        JIRA_DOMAIN: domain || "",
        JIRA_EMAIL: email || "",
      }),
    }),
    productVariant({
      identifier: "jiraServiceAccountApiToken",
      authLabel: "Service account API token",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ cloudId }) =>
        atlassianGatewayPattern("jira", cloudId),
      auth: (app) =>
        basicAuth({
          username: app.config.email,
          password: app.secret.apiToken,
        }),
      steps: atlassianServiceAccountApiTokenSteps("Jira", "read:jira-user"),
      connectionTest: {
        protocol: "http",
        request: ({ cloudId }) =>
          `${atlassianGatewayBaseUrl("jira", cloudId || "cloud-id")}/rest/api/3/myself`,
      },
      publicEnvVars: ({ cloudId }) => ({
        JIRA_BASE_URL: atlassianGatewayBaseUrl("jira", cloudId),
      }),
      usageHint: () =>
        "Use process.env.JIRA_BASE_URL as the Atlassian gateway base URL for Jira API requests.",
    }),
    productVariant({
      identifier: "jiraServiceAccountOAuth",
      authLabel: "Service account OAuth 2.0",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ cloudId }) =>
        atlassianGatewayPattern("jira", cloudId),
      auth: oauthAuth,
      steps: atlassianServiceAccountOAuthSteps("Jira", "read:jira-user"),
      oauth: atlassianServiceAccountOAuth,
      connectionTest: {
        protocol: "http",
        request: ({ cloudId }) =>
          `${atlassianGatewayBaseUrl("jira", cloudId || "cloud-id")}/rest/api/3/myself`,
      },
      publicEnvVars: ({ cloudId }) => ({
        JIRA_BASE_URL: atlassianGatewayBaseUrl("jira", cloudId),
      }),
      usageHint: () =>
        "Use process.env.JIRA_BASE_URL as the Atlassian gateway base URL for Jira API requests.",
    }),
  ],
});
